import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { AppSelect } from '../../../components/common/select';
import api from '../../../api';
import { fetchServiceOptions, fetchUserOptions, fetchRoleOptions, fetchUsersByRoleId } from '../api/workflowApi'; // sesuaikan path
import useModalEffect from '../../../hooks/useModalEffect';

const ApprovalWorkflowForm = ({ isOpen, onClose, onSubmit, initialData }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState({
    workflowName: '',
    service_id: '', // sesuaikan dengan entity
    settings: [
      {
        step: 1,
        role: '',
        userIds: [], // array
        rule: 'initiator'
      }
    ]
  });
  const [formErrors, setFormErrors] = useState({});

  // PATCH: Mode create/edit
  const isEditMode = Boolean(initialData);

  // PATCH: Reset form saat modal dibuka/tutup
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          workflowName: initialData.workflowName,
          service_id: initialData.service_id,
          settings: initialData.settings.map(setting => ({
            step: setting.step,
            role: setting.role,
            // PATCH: mapping dari array of user object ke array of id
            userIds: Array.isArray(setting.users)
              ? setting.users.map(u => u.id)
              : [],
            rule: setting.rule,
            moveTo: setting.moveTo ?? '' // <-- TAMBAHKAN INI
          }))
        });
      } else {
        setFormData({
          workflowName: '',
          service_id: '',
          settings: [
            {
              step: 1,
              role: '',
              userIds: [],
              rule: 'initiator'
            }
          ]
        });
      }
      setFormErrors({});
    }
  }, [isOpen, initialData]);

  // Service options for AppSelect (ubah ke state)
  const [serviceOptions, setServiceOptions] = useState([]);

  // Ambil data service dari API
  useEffect(() => {
    const getServiceOptions = async () => {
      try {
        const options = await fetchServiceOptions();
        setServiceOptions(options);
      } catch (err) {
        setServiceOptions([]);
      }
    };
    getServiceOptions();
  }, []);

  // Role options (job titles)
  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    const getRoleOptions = async () => {
      try {
        const options = await fetchRoleOptions();
        setRoleOptions(options);
        // console.log('roleOptions:', options); // <-- Tambahkan log ini
      } catch (err) {
        setRoleOptions([]);
        console.error('Error fetchRoleOptions:', err); // <-- Tambahkan log ini
      }
    };
    getRoleOptions();
  }, []);

  // Static user list for demo
  const [userOptions, setUserOptions] = useState([]);

  useEffect(() => {
    const getUserOptions = async () => {
      try {
        const options = await fetchUserOptions();
        setUserOptions(options);
        // console.log('userOptions:', options); // <-- log ini
      } catch (err) {
        setUserOptions([]);
        console.error('Error fetchUserOptions:', err);
      }
    };
    getUserOptions();
  }, []);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSettingChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: prev.settings.map((setting, i) => 
        i === index ? { ...setting, [field]: value } : setting
      )
    }));
  };

  // Get user options for a given role
  const getUserOptionsForRole = (role) => {
    const filtered = userOptions.filter(u => u.role === role);
    // console.log('role:', role, 'filtered:', filtered); // <-- log ini
    return filtered;
  };

  const addWorkflowSetting = () => {
    setFormData(prev => ({
      ...prev,
      settings: [
        ...prev.settings,
        {
          step: prev.settings.length + 1,
          role: '',
          userIds: [],
          rule: 'approver'
        }
      ]
    }));
  };

  const deleteWorkflowSetting = (index) => {
    if (formData.settings.length === 1) return; // Don't delete if it's the last setting
    
    setFormData(prev => ({
      ...prev,
      settings: prev.settings
        .filter((_, i) => i !== index)
        .map((setting, i) => ({
          ...setting,
          step: i + 1,
          rule: i === 0 ? 'initiator' : 'approver'
        })) // Reorder steps and set rule
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.workflowName.trim()) {
      errors.workflowName = 'Workflow name is required';
    }
    if (!formData.service_id) {
      errors.service_id = 'Service is required';
    }
    
    // Validate settings
    const settingsErrors = formData.settings.map(setting => {
      const settingError = {};
      if (!setting.role) {
        settingError.role = 'Role is required';
      }
      if ((!setting.userIds || setting.userIds.length === 0) && setting.step !== 1) {
        settingError.userIds = 'User is required';
      }
      return Object.keys(settingError).length > 0 ? settingError : null;
    });

    if (settingsErrors.some(error => error !== null)) {
      errors.settings = settingsErrors;
    }

    // console.log(JSON.stringify(errors))
    return errors;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      onSubmit(formData);
      setFormData({
        workflowName: '',
        service_id: '',
        settings: [
          {
            step: 1,
            role: '',
            userIds: [],
            rule: 'initiator'
          }
        ]
      });
    } else {
      setFormErrors(errors);
    }
  };

  const handleModalClose = () => {
    setFormData({
      workflowName: '',
      service_id: '',
      settings: [
        {
          step: 1,
          role: '',
          userIds: [],
          rule: 'initiator'
        }
      ]
    });
    setFormErrors({});
    onClose();
  };

  // Generate move to options based on steps (not roles)
  const getMoveToStepOptions = (currentStepIndex) => {
    const currentStepNumber = formData.settings[currentStepIndex].step;
    // Only allow moving to previous steps (step number less than current)
    return formData.settings
      .filter((setting, i) => setting.step < currentStepNumber)
      .map(setting => ({
        id: setting.step,
        name: `Step ${setting.step}`
      }));
  };

  const [userOptionsByStep, setUserOptionsByStep] = useState({});

  useEffect(() => {
    if (isOpen && initialData) {
      // Untuk setiap step, fetch user by role jika role sudah ada
      initialData.settings.forEach(async (setting, index) => {
        if (setting.role) {
          // Temukan roleId dari roleOptions
          const selectedRole = roleOptions.find(r => r.value === setting.role);
          const roleId = selectedRole ? selectedRole.id : null;
          if (roleId) {
            const users = await fetchUsersByRoleId(roleId);
            setUserOptionsByStep(prev => ({
              ...prev,
              [index]: users
            }));
          }
        }
      });
    }
  }, [isOpen, initialData, roleOptions]);

  const handleRoleChange = async (index, roleValue) => {
    // Cari roleId dari roleOptions
    const selectedRole = roleOptions.find(r => r.value === roleValue);
    const roleId = selectedRole ? selectedRole.id : null;

    // Update role di formData
    handleSettingChange(index, 'role', roleValue);

    // Fetch user options jika roleId ada
    if (roleId) {
      const users = await fetchUsersByRoleId(roleId);
      setUserOptionsByStep(prev => ({
        ...prev,
        [index]: users
      }));
    } else {
      setUserOptionsByStep(prev => ({
        ...prev,
        [index]: []
      }));
    }
  };

  // PATCH: Modal title and button
  ;

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-backdrop');
      };
      
      cleanupModal();
    };
  }, []);


  // Cleanup effect: reset modal body state when component unmounts.
  useEffect(() => {
    return () => {
      // React owns the backdrop DOM (createPortal); only reset body state here.
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
    };
  }, []);

  return (
    <>
      <style>{`
        #modal-approval-workflow .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-approval-workflow .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-approval-workflow .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-approval-workflow .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-approval-workflow .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-approval-workflow .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-approval-workflow"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {isEditMode ? 'Edit Workflow' : 'Create Workflow'}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="inv-section-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  Details
                </div>
                <div className="mb-3">
                  <AppSelect
                    name="service_id"
                    value={formData.service_id}
                    onChange={handleInputChange}
                    options={serviceOptions}
                    placeholder="Select service"
                    required
                    error={formErrors.service_id}
                    label="Service"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Workflow Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.workflowName ? 'is-invalid' : ''}`}
                    name="workflowName"
                    value={formData.workflowName}
                    onChange={(e) => handleInputChange('workflowName', e.target.value)}
                    placeholder="Enter workflow name"
                  />
                  {formErrors.workflowName && (
                    <div className="invalid-feedback">{formErrors.workflowName}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Workflow Settings</label>
                  {formData.settings.map((setting, index) => (
                    <div key={index} className="card mb-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h4 className="card-title m-0">Step {setting.step}</h4>
                        </div>
                        <div className="row g-3">
                          <div className="col-md-2">
                            <label className="form-label">Step Number</label>
                            <input
                              type="number"
                              className="form-control"
                              value={setting.step}
                              readOnly
                            />
                          </div>
                          <div className="col-md-3">
                            <AppSelect
                              name={`role-${index}`}
                              value={setting.role}
                              onChange={(_, value) => handleRoleChange(index, value)}
                              options={roleOptions}
                              valueField="value"
                              labelField="label"
                              placeholder="Select role"
                              required
                              error={formErrors.settings?.[index]?.role}
                              label="Role (Job Title)"
                            />
                          </div>
                          <div className="col-md-4">
                            <AppSelect
                              name={`userIds-${index}`}
                              label="User"
                              value={setting.userIds || []}
                              onChange={(_, value) => {
                                // Pastikan value selalu array of number
                                let arr = Array.isArray(value) ? value : value ? [value] : [];
                                handleSettingChange(index, 'userIds', arr.map(Number));
                              }}
                              options={userOptionsByStep[index] || []}
                              placeholder="Select user"
                              required={setting.step !== 1}
                              error={formErrors.settings?.[index]?.userIds}
                              disabled={!setting.role}
                              valueField="value"
                              labelField="label"
                              searchField="label"
                              multiple={true}
                            />
                          </div>
                          <div className="col-md-3">
                            <AppSelect
                              name={`moveTo-${index}`}
                              value={setting.moveTo}
                              onChange={(_, value) => handleSettingChange(index, 'moveTo', value)}
                              options={getMoveToStepOptions(index)}
                              placeholder="Move to step"
                              required={setting.step !== 1}
                              error={formErrors.settings?.[index]?.moveTo}
                              label="Move To (If Need Revision)"
                              disabled={index === 0}
                            />
                          </div>
                          
                          {/* REMOVED: User for Revision field */}
                          <div className="col-11">
                            <label className="form-label">Rule</label>
                            <div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={setting.step === 1 ? setting.rule === 'initiator' : setting.rule === 'approver'}
                                  disabled
                                />
                                <span className="form-check-label">
                                  {setting.step === 1 ? 'Initiator' : 'Approver'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-1 d-flex align-items-end justify-content-center">
                            {formData.settings.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-icon btn-ghost-danger"
                                onClick={() => deleteWorkflowSetting(index)}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                  <path d="M4 7l16 0"></path>
                                  <path d="M10 11l0 6"></path>
                                  <path d="M14 11l0 6"></path>
                                  <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                                  <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={addWorkflowSetting}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M12 5l0 14"></path>
                      <path d="M5 12l14 0"></path>
                    </svg>
                    Add Step
                  </button>
                </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  <span style={{ color: '#9aa5b1' }}>
                    <span className="inv-required-star">*</span> Required fields
                  </span>
                </span>
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-inv-save">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {isEditMode ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

ApprovalWorkflowForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object // Added initialData prop
};

export default ApprovalWorkflowForm; 